import React, { useState, useEffect } from "react";
import Button from "../../components/Button/Button";
import { useLazyQuery } from "@apollo/client";
import Modal from "../../components/modal/Modal";
import { GET_PACKAGES_QUERY } from "../../graphql/queries/packages";
import Loader from "../../components/loader/loader";
import Table from "../../components/table/table";
import TableCellRenderer from "../../components/table/table-cell-renderer";
import AddPackage from "../../components/add-package/add-package";
import { FIRST, SKIP } from "../../utils/pagination";
import EditPackage from "../../components/edit-package/edit-package";
import useUserRoleStore from "../../store/user-role";
import { isAdmin } from "../../utils/roles";

const Packages = () => {
  const [addPackageModal, setAddPackageModal] = useState(false);
  const [loadingPage, setLoadingPage] = useState(false);
  const [editPackageModal, setEditPackageModal] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [paginationInfo, setPaginationInfo] = useState({
    hasNextPage: undefined,
    hasPreviousPage: undefined,
    totalPages: undefined,
    totalRecords: undefined,
    currentPage: 1,
  });

  const { role } = useUserRoleStore();

  const tableActions = !isAdmin(role)
    ? []
    : [
        {
          name: "Edit",
          onClick: (pkg) => {
            setSelectedPackage(pkg);
            setEditPackageModal(true);
          },
          shouldShow: () => true,
        },
      ];

  const [fetchPackages, { data, loading, error }] = useLazyQuery(
    GET_PACKAGES_QUERY,
    {
      variables: {
        first: FIRST,
        skip: (paginationInfo.currentPage - 1) * SKIP,
      },
      fetchPolicy: "network-only",
    }
  );

  const onAddPackageClose = () => {
    setLoadingPage(true);
    setAddPackageModal(false);
  };

  const onAddPackage = () => {
    setAddPackageModal(true);
  };

  useEffect(() => {
    if (data) {
      setPaginationInfo((prev) => ({
        ...prev,
        currentPage: data.packages.currentPage,
        hasNextPage: data.packages.hasNextPage,
        hasPreviousPage: data.packages.hasPreviousPage,
        totalPages: data.packages.totalPages,
        totalRecords: data.packages.totalRecords,
      }));
    }
  }, [data]);

  const onNextPageClick = () => {
    setPaginationInfo((prev) => ({
      ...prev,
      currentPage: prev?.currentPage + 1,
    }));
  };

  const onPrevPageClick = () => {
    setPaginationInfo((prev) => ({
      ...prev,
      currentPage: prev?.currentPage - 1,
    }));
  };

  useEffect(() => {
    fetchPackages();
  }, []);

  useEffect(() => {
    if (loadingPage) {
      fetchPackages();
      setLoadingPage(false);
    }
  }, [loadingPage]);

  if (error) {
    return <div className="display-page__error">Something went wrong</div>;
  }

  return (
    <div className="display-page">
      <Modal
        modal={editPackageModal}
        onClose={() => setEditPackageModal(false)}
        modalSize="large"
      >
        <EditPackage
          onCancel={() => setEditPackageModal(false)}
          packageData={selectedPackage}
        />
      </Modal>
      <Modal
        modal={addPackageModal}
        onClose={onAddPackageClose}
        modalSize="large"
      >
        <>
          <AddPackage onCancel={onAddPackageClose} />
        </>
      </Modal>
      <div className="display-page__header">
        {isAdmin(role) && (
          <Button
            onClick={onAddPackage}
            name={"+ Add Package"}
            className={"button--primary"}
          />
        )}
      </div>
      <div className="display-page__info-wrapper">
        Displaying{" "}
        <span className="display-page__info--bold">
          {paginationInfo.currentPage === 1
            ? 1
            : (paginationInfo.currentPage - 1) * SKIP + 1}{" "}
        </span>
        to{" "}
        <span className="display-page__info--bold">
          {Math.min(
            paginationInfo.currentPage * SKIP,
            paginationInfo.totalRecords
          )}
        </span>{" "}
        of{" "}
        <span className="display-page__info--bold">
          {paginationInfo.totalRecords}
        </span>{" "}
        total.
      </div>
      <div className="details-page__table">
        {loading || loadingPage ? (
          <Loader sizeClass={"size--medium"} />
        ) : (
          <Table
            actions={tableActions}
            data={data?.packages?.items}
            columns={[
              {
                field: "name",
                title: "Name",
                showOnMobile: true,
                hideMobileUp: false,
                render: ({ name }) => {
                  return <TableCellRenderer firstItem={name} />;
                },
              },
              {
                field: "price",
                title: "Price",
                showOnMobile: true,
                hideMobileUp: false,
                render: ({ price }) => {
                  return (
                    <TableCellRenderer firstItem={`R${price.toFixed(2)}`} />
                  );
                },
              },
              {
                field: "limitDailywh",
                title: "Daily Limit",
                showOnMobile: true,
                hideMobileUp: false,
                render: ({ limitDailywh }) => {
                  return (
                    <TableCellRenderer
                      firstItem={`${limitDailywh.toFixed(2)}Wh`}
                    />
                  );
                },
              },
              {
                field: "limitMamps",
                title: "Current Limit (mA)",
                showOnMobile: true,
                hideMobileUp: false,
                render: ({ limitMamps }) => {
                  return (
                    <TableCellRenderer
                      firstItem={`${limitMamps.toFixed(2)}mA`}
                    />
                  );
                },
              },
              {
                field: "resetTime",
                title: "Reset Time",
                showOnMobile: true,
                hideMobileUp: false,
                render: ({ resetTime }) => {
                  return (
                    <TableCellRenderer firstItem={`${resetTime.toFixed(2)}`} />
                  );
                },
              },
            ]}
            paged
            hasNextPage={paginationInfo.hasNextPage}
            hasPrevPage={paginationInfo.hasPreviousPage}
            onNextPage={onNextPageClick}
            onPrevPage={onPrevPageClick}
          />
        )}
      </div>
    </div>
  );
};

export default Packages;
